import {
  getDefaultGridFilterModel,
  GridFilterItem,
  GridFilterModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import React from 'react';

// Mapping of Material UI grid operators to backend operators.
const gridToApiOperatorMap: Record<string, string> = {
  is: 'equal',
  isAnyOf: 'in',
  isEmpty: 'isNull',
  isNotEmpty: 'isNotNull',
  after: 'moreThan',
  onOrAfter: 'moreThanOrEqual',
  before: 'lessThan',
  onOrBefore: 'lessThanOrEqual',
  contains: 'like',
  startsWith: 'like',
  endsWith: 'like',
  '=': 'equal',
  '!=': 'notEqual',
  '<': 'lessThan',
  '<=': 'lessThanOrEqual',
  '>': 'moreThan',
  '>=': 'moreThanOrEqual',
};

export interface ApiFilters {
  [key: string]: {
    value: any;
    operator: string;
  };
}

// Mapping of backend operators to Material UI grid operators.
const apiToGridOperatorMap: Record<string, string> = {
  equal: 'is',
  in: 'isAnyOf',
  isNull: 'isEmpty',
  isNotNull: 'isNotEmpty',
  moreThan: 'after',
  moreThanOrEqual: 'onOrAfter',
  lessThan: 'before',
  lessThanOrEqual: 'onOrBefore',
  like: 'contains',
  notEqual: '!=',
};

export function mapApiFiltersToGridFilterItems(initialFilterState?: ApiFilters): GridFilterItem[] {
  if (!initialFilterState) {
    return [];
  }

  return Object.entries(initialFilterState).map(([field, value]) => ({
    field,
    id: field,
    value: value.value,
    operator: apiToGridOperatorMap[value.operator] ?? 'is',
  }));
}

export function useTableApiQueryFilterState<T extends { [key: string]: any }>(
  initialFilterState?: ApiFilters
) {
  const gridApiRef = useGridApiRef();

  const [apiFilters, setApiFilters] = React.useState<ApiFilters | undefined>(initialFilterState);

  const onFilterModelChange = React.useCallback((filterModel: GridFilterModel) => {
    if (filterModel.items.length > 0) {
      const newApiFilters = filterModel.items.reduce((acc: ApiFilters, curr) => {
        acc[curr.field] = {
          value: curr.value,
          operator: curr.operator ? (gridToApiOperatorMap[curr.operator] ?? 'equal') : 'equal',
        };
        return acc;
      }, {});

      setApiFilters(newApiFilters);
    } else {
      setApiFilters(undefined);
    }
  }, []);

  const onFilterInputChange = React.useCallback(
    (entityField: keyof T, value: any | null | undefined, operator?: string) => {
      const field = entityField as string;
      if (gridApiRef.current) {
        if (value !== undefined && value !== null) {
          gridApiRef.current.upsertFilterItem({
            field,
            value: value,
            id: field,
            operator: operator ?? 'is',
          });
        } else {
          gridApiRef.current.deleteFilterItem({ field, id: field, operator: 'is' });
        }
      }
    },
    [gridApiRef]
  );

  const clearFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(getDefaultGridFilterModel());
    }
  };

  return {
    gridApiRef,
    apiFilters,
    onFilterModelChange,
    onFilterInputChange,
    clearFilters,
  };
}
